import { IconName } from "@inflow/icons"
import { useQuery } from "@tanstack/react-query"
import {
  Link,
  useLocation,
  useMatchRoute,
  useRouterState,
} from "@tanstack/react-router"
import { useEffect } from "react"
import {
  Dialog,
  ModalOverlay,
  ModalOverlayProps,
  Modal as RACModal,
} from "react-aria-components"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button, IconButton, LinkButton } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Separator from "@/globalComponents/atoms/Separator"
import Text from "@/globalComponents/atoms/Text"
import LanguageSwitcherModal from "@/globalComponents/molecules/LanguageSwitcherModal"
import { ENV } from "@/utilities/config"
import { upperFirst } from "@/utilities/etc"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import { useTheme } from "@/utilities/hooks/useTheme"
import useToggle from "@/utilities/hooks/useToggle"
import { getLanguageName, iconForLanguage } from "@/utilities/localization"
import { ScopedPath, scopedTo } from "@/utilities/scopedRoutes"

import styles from "./Drawer.module.css"

export function Drawer(props: ModalOverlayProps) {
  const { t, i18n } = useTranslation()
  const { qrLookupID, welcome, drawer, documentThemeMode } = useTheme()
  const { data: me } = useQuery(meQueryOptions())
  const routerState = useRouterState()
  const location = useLocation()
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useToggle(false)

  const hasFlexPurchase = welcome?.flowCard.flows.some(
    (flow) => flow.type === "flexPurchase"
  )
  const hasReserve = welcome?.flowCard.flows.some(
    (flow) => flow.type === "reserve"
  )

  const themedQrLookupID =
    documentThemeMode === "whiteLabel" ? qrLookupID : undefined

  const matchRoute = useMatchRoute()
  const scoped = ({ to }: { to: ScopedPath }) => scopedTo(to, themedQrLookupID)
  const matchesAccountRoute = (to: ScopedPath) =>
    !!matchRoute({ ...scoped({ to }), fuzzy: true })

  const isPassesRoute = matchesAccountRoute("/account/passes")
  const isVehiclesRoute = matchesAccountRoute("/account/vehicles")
  const isReservationsRoute = matchesAccountRoute("/account/reservations")
  const isMembershipsRoute = matchesAccountRoute("/account/memberships")

  /**
   * "Account" matches fuzzily so it highlights on account pages with no drawer
   * entry of their own, and exactly on sub-sections that have one.
   */
  const isOnAccountSubsection =
    isPassesRoute ||
    isVehiclesRoute ||
    isReservationsRoute ||
    isMembershipsRoute

  useEffect(() => {
    if (routerState.status === "pending" && props.isOpen === true) {
      props.onOpenChange?.(false)
    }
  }, [routerState.status, props.isOpen])

  return (
    <ModalOverlay className="nav-drawer--overlay" isDismissable {...props}>
      <RACModal className="nav-drawer">
        <Dialog className="nav-drawer--inner">
          <div className="nav-drawer--header">
            <Heading level={2} slot="title">
              {me?.cognitoUser
                ? t("navigation.drawer.authenticatedHeader", {
                    firstName: me.cognitoUser.given_name,
                  })
                : t("navigation.drawer.header")}
            </Heading>
            <IconButton
              buttonType="primary"
              presentation="ghost"
              iconName="close"
              aria-label={t("common.close")}
              onPress={() => props.onOpenChange?.(false)}
            />
          </div>
          <div className="nav-drawer--links">
            <ul>
              <li className="body--1">
                <DrawerLink
                  qrLookupID={themedQrLookupID}
                  to="/"
                  iconName="home"
                  label={t("common.home")}
                />
              </li>
              {documentThemeMode === "whiteLabel" &&
                drawer?.externalLinks?.map((link, index) => (
                  <li className="body--1" key={index}>
                    {link.type === "rates" ? (
                      <a href={link.link} target="_blank" rel="noreferrer">
                        <Icon name={link.icon as IconName} slot="left" />
                        {t("navigation.drawer.rates")}
                      </a>
                    ) : (
                      <></>
                    )}
                  </li>
                ))}
              <li className="body--1">
                <DrawerLink
                  qrLookupID={themedQrLookupID}
                  to="/help"
                  iconName="questionMark"
                  label={t("navigation.drawer.help")}
                />
              </li>
              <li className="body--1">
                <DrawerLink
                  qrLookupID={themedQrLookupID}
                  to="/policies"
                  iconName="file"
                  label={t("navigation.drawer.policies")}
                />
              </li>
              <li className="body--1">
                <button
                  onClick={() => setIsLanguageModalOpen()}
                  className={styles.languageButton}
                >
                  <Icon name="language" />
                  <span className={styles.languageButtonText}>
                    {t("navigation.drawer.languages")}
                  </span>
                  <Text level={3} className={styles.languageButtonName}>
                    {getLanguageName(i18n.resolvedLanguage)}
                  </Text>
                  <Icon
                    name={iconForLanguage(i18n.resolvedLanguage)}
                    retainOriginalColors={
                      iconForLanguage(i18n.resolvedLanguage) !== "language"
                    }
                    className={styles.languageButtonFlag}
                  />
                </button>
              </li>
            </ul>
            {me?.cognitoUser && (
              <>
                <Separator
                  orientation="horizontal"
                  style={{ marginLeft: "var(--space-4)" }}
                />
                <ul>
                  <li className="body--1">
                    <DrawerLink
                      qrLookupID={themedQrLookupID}
                      to="/account"
                      iconName="person"
                      label={t("navigation.drawer.account")}
                      activeOptions={{ exact: isOnAccountSubsection }}
                    />
                  </li>
                  {hasFlexPurchase && (
                    <li className="body--1">
                      <DrawerLink
                        qrLookupID={themedQrLookupID}
                        to="/account/passes"
                        iconName="ticket"
                        label={t("navigation.drawer.passes")}
                      />
                    </li>
                  )}
                  <li className="body--1">
                    <DrawerLink
                      qrLookupID={themedQrLookupID}
                      to="/account/vehicles"
                      iconName="car"
                      label={t("navigation.drawer.vehicles")}
                    />
                  </li>
                  {hasReserve && (
                    <li className="body--1">
                      <DrawerLink
                        qrLookupID={themedQrLookupID}
                        to="/account/reservations"
                        iconName="calendar"
                        label={t("navigation.drawer.reservations")}
                      />
                    </li>
                  )}
                  {hasReserve && (
                    <li className="body--1">
                      <DrawerLink
                        qrLookupID={themedQrLookupID}
                        to="/account/memberships"
                        iconName="key"
                        label={t("navigation.drawer.keysAndMemberships")}
                      />
                    </li>
                  )}
                </ul>
              </>
            )}
          </div>
          {
            <div className="nav-drawer--footer">
              {me?.cognitoUser ? (
                <Button
                  onPress={() => api.auth.signOut()}
                  buttonType="danger"
                  presentation="ghost"
                >
                  <Icon name="exit" slot="left" />
                  {t("navigation.drawer.signOut")}
                </Button>
              ) : (
                <>
                  <LinkButton
                    buttonType="default"
                    to="/auth/sign-in"
                    search={{
                      redirect: location.href,
                      qrLookupID: themedQrLookupID,
                      plate: undefined,
                      phone: undefined,
                      email: undefined,
                      conversionFlow: undefined,
                    }}
                  >
                    {t("navigation.drawer.signIn")}
                  </LinkButton>
                  <LinkButton
                    buttonType="primary"
                    to="/auth/create-account"
                    search={{
                      redirect: location.href,
                      qrLookupID: themedQrLookupID,
                      plate: undefined,
                      phone: undefined,
                      email: undefined,
                      conversionFlow: undefined,
                    }}
                  >
                    {t("navigation.drawer.createAccount")}
                  </LinkButton>
                </>
              )}
            </div>
          }
          {me && (
            <Text
              level={3}
              className="nav-drawer--app-version"
              style={{ paddingBottom: "var(--space-1)" }}
            >
              {t("navigation.drawer.loggedInAs", {
                email: me.cognitoUser.email,
              })}
            </Text>
          )}
          <Text level={3} className="nav-drawer--app-version">
            {ENV !== "production" && `${upperFirst(ENV)} `}Version {APP_VERSION}
          </Text>
        </Dialog>
      </RACModal>
      <LanguageSwitcherModal
        isOpen={isLanguageModalOpen}
        onOpenChange={setIsLanguageModalOpen}
      />
    </ModalOverlay>
  )
}

/**
 * Drawer nav link. Targets the `/qr/$qrLookupID` tree when given a
 * `qrLookupID`, the top-level tree otherwise.
 */
function DrawerLink({
  qrLookupID,
  to,
  iconName,
  label,
  activeOptions,
}: {
  qrLookupID?: string
  to: ScopedPath
  iconName: IconName
  label: string
  activeOptions?: { exact: boolean }
}) {
  return (
    <Link {...scopedTo(to, qrLookupID)} activeOptions={activeOptions}>
      <Icon name={iconName} slot="left" />
      {label}
    </Link>
  )
}

export default Drawer
